import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

// Branded Midemide truck — black Cascadia with the M-logo on the trailer at sunset
const HERO_BG = "/midemide-truck-hero.webp";

export default function Hero() {
  const { t } = useT();

  return (
    <section data-testid="hero-section" className="relative w-full bg-white">
      {/* Split layout: dark image (60%) + white panel (40%) on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[94vh]">
        {/* DARK SIDE — image + headline */}
        <div className="relative lg:col-span-7 min-h-[60vh] lg:min-h-[94vh] flex items-end overflow-hidden bg-[#0a0a0a]">
          <div
            className="absolute inset-0 bg-cover scale-105"
            style={{
              backgroundImage: `url(${HERO_BG})`,
              backgroundPosition: "right center",
            }}
          />
          {/* Heavier left-to-right gradient — hero blends into the black header/logo lockup while keeping the truck faintly visible on the right */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(10,10,10,1) 0%, rgba(10,10,10,0.98) 30%, rgba(10,10,10,0.85) 55%, rgba(10,10,10,0.6) 80%, rgba(10,10,10,0.4) 100%)",
            }}
          />
          <div className="absolute inset-0 grain pointer-events-none opacity-50" />

          {/* Side ticker */}
          <div className="hidden md:flex absolute left-8 top-1/2 -rotate-90 origin-left items-center gap-3 z-10">
            <span className="block w-12 h-px bg-[#D9633F]" />
            <span className="font-mono text-xs text-[#D9633F] tracking-[0.4em] uppercase">MDLGX · CANADA · USA</span>
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-14 pt-44 sm:pt-48 lg:pt-32 pb-14 lg:pb-20">
            <h1
              data-testid="hero-headline"
              className="font-display font-extrabold text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05] fade-up break-words"
              style={{ animationDelay: "0.1s" }}
            >
              {t("hero.headline1")}<br />
              <span className="text-[#D9633F]">{t("hero.headline2")}</span><br />
              {t("hero.headline3")}
            </h1>

            <p
              data-testid="hero-sub"
              className="text-white/85 mt-6 md:mt-8 max-w-2xl leading-relaxed fade-up"
              style={{ animationDelay: "0.25s" }}
            >
              {t("hero.subtitle")}
            </p>

            <div
              className="inline-flex items-center gap-2 sm:gap-3 mt-7 sm:mt-8 fade-up bg-[#D9633F] text-white px-4 sm:px-5 py-2.5 sm:py-3 shadow-[0_8px_24px_rgba(217,99,63,0.35)] max-w-full"
              style={{ animationDelay: "0.35s" }}
            >
              <span className="hidden sm:block w-8 h-px bg-white/80" />
              <span className="font-display font-extrabold text-xs sm:text-sm md:text-base tracking-[0.2em] sm:tracking-[0.25em] uppercase whitespace-nowrap">
                {t("hero.badge")}
              </span>
            </div>

            <div className="flex flex-wrap gap-3 md:gap-4 mt-7 md:mt-8 fade-up" style={{ animationDelay: "0.45s" }}>
              <Link to="/quote" data-testid="hero-quote-cta" className="btn-sharp btn-primary text-sm md:text-base px-7 md:px-9 py-4 md:py-5">
                {t("hero.requestQuote")} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-dispatch-cta" className="btn-sharp btn-ghost text-sm md:text-base px-7 md:px-9 py-4 md:py-5">
                <Phone size={16} /> {t("hero.contactDispatch")}
              </Link>
            </div>
          </div>
        </div>

        {/* WHITE SIDE — dispatch panel + stats */}
        <div className="relative lg:col-span-5 bg-white text-neutral-900 px-6 md:px-10 lg:px-14 py-12 lg:py-20 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-neutral-200">
          {/* Decorative diagonal grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #0a0a0a 0, #0a0a0a 1px, transparent 1px, transparent 18px)",
            }}
          />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-[#D9633F]" />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#D9633F]">
                {t("hero.panelOverline")}
              </span>
            </div>

            <h2
              data-testid="hero-white-title"
              className="font-display font-extrabold text-neutral-900 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.08]"
            >
              {t("hero.panelTitle")}
            </h2>
            <p className="text-neutral-600 mt-5 max-w-md leading-relaxed">
              {t("hero.panelSub")}
            </p>

            {/* Contact rows */}
            <div className="mt-8 space-y-px bg-neutral-200">
              <a
                data-testid="hero-call-row"
                href="tel:+19053637673"
                className="flex items-center justify-between bg-white px-5 py-4 group hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Phone size={18} className="text-[#D9633F]" />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">{t("hero.panelCallNow")}</div>
                    <div className="font-display font-bold text-neutral-900 text-lg leading-tight">+1 (905) 363-7673</div>
                  </div>
                </div>
                <ArrowRight size={16} className="text-neutral-400 group-hover:text-[#D9633F] group-hover:translate-x-1 transition-all" />
              </a>
              <a
                href="tel:+12893011919"
                className="flex items-center justify-between bg-white px-5 py-4 group hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Phone size={18} className="text-[#D9633F]" />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">GTA</div>
                    <div className="font-display font-bold text-neutral-900 text-lg leading-tight">+1 (289) 301-1919</div>
                  </div>
                </div>
                <ArrowRight size={16} className="text-neutral-400 group-hover:text-[#D9633F] group-hover:translate-x-1 transition-all" />
              </a>
              <a
                data-testid="hero-email-row"
                href="mailto:dispatch@midemidelogistics.com"
                className="flex items-center justify-between bg-white px-5 py-4 group hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Mail size={18} className="text-[#D9633F]" />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">{t("hero.panelEmailUs")}</div>
                    <div className="font-display font-bold text-neutral-900 text-base leading-tight">dispatch@midemidelogistics.com</div>
                  </div>
                </div>
                <ArrowRight size={16} className="text-neutral-400 group-hover:text-[#D9633F] group-hover:translate-x-1 transition-all" />
              </a>
              <div className="flex items-center gap-4 bg-white px-5 py-4">
                <MapPin size={18} className="text-[#D9633F]" />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">HQ</div>
                  <div className="text-neutral-700 text-sm leading-tight">Ontario, Canada · serving CA & USA</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip — black-on-white */}
          <div className="relative z-10 mt-12 grid grid-cols-2 gap-px bg-neutral-200">
            {[
              { k: t("hero.stat1k"), v: t("hero.stat1v") },
              { k: t("hero.stat2k"), v: t("hero.stat2v") },
              { k: t("hero.stat3k"), v: t("hero.stat3v") },
              { k: t("hero.stat4k"), v: t("hero.stat4v") },
            ].map((s) => (
              <div key={s.v} className="bg-white p-5 md:p-6">
                <div className="font-display font-extrabold text-neutral-900 text-2xl md:text-3xl lg:text-4xl tracking-tight">
                  {s.k}
                </div>
                <div className="text-neutral-500 text-[10px] mt-1.5 tracking-[0.2em] uppercase">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
